import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Card, DatePicker, Spin, Table, Tabs } from "antd";
import { DoubleLeftOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import moment from "moment";
import KycService from "../../services/kyc-service";
import "./KYC-Dashboard.css";
import {
	KYCGroupCompanyImage,
	KYCContactsImage,
	KYCCylindersImage,
	KYCDesignImage,
	KYCMaterialsImage,
	KYCBusinessOverviewImage,
} from "../Elite/imageAssets";

const { TabPane } = Tabs;

const defaultPayload = {
	fromDate: "2026-04-09T12:10:21.201Z",
	toDate: "2026-04-09T12:10:21.201Z",
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const createdOnFormats = [
	"MM/DD/YYYY HH:mm:ss",
	"MM/DD/YYYY H:mm:ss",
	"M/D/YYYY HH:mm:ss",
	"M/D/YYYY H:mm:ss",
	"M/D/YYYY h.mm.ss A",
	"M/D/YYYY h:mm:ss A",
	moment.ISO_8601,
];

const formatDate = (value) => {
	if (!value) {
		return "-";
	}
	const parsedDate = moment(value, createdOnFormats, true);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "-";
};

const parseCreatedOnDate = (value) => moment(value, createdOnFormats, true);

const getValue = (record, keys) => {
	for (const key of keys) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}
	return "-";
};

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const KycPartnerFunctionDetails = () => {
	const { crmGuid } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [isCardsLoading, setIsCardsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState("partnerFunction");
	const [partnerFunctionRows, setPartnerFunctionRows] = useState([]);
	const [tableSorter, setTableSorter] = useState({ field: "", order: "" });
	const [onboardDate, setOnboardDate] = useState(null);
	const [associationYear, setAssociationYear] = useState(null);
	const [partnerFunctionCount, setPartnerFunctionCount] = useState({});
	const [contactsCount, setContactsCount] = useState({});
	const [materialsCount, setMaterialsCount] = useState({});
	const [designsCount, setDesignsCount] = useState({});
	const [cylindersCount, setCylindersCount] = useState({});
	const [businessOverviewCount, setBusinessOverviewCount] = useState({});

	const soldToPartyName = location.state?.accountName || "Sold to Party Name";

	const getCardTitle = (countObj, label, keys = ["total"]) => {
		const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
		return countValue !== undefined ? `${countValue} ${label}` : label;
	};

	const getSortOrder = (tableSorter, columnKey) => {
		if (tableSorter.field !== columnKey) {
			return null;
		}

		return tableSorter.order === "ASC" ? "ascend" : tableSorter.order === "DESC" ? "descend" : null;
	};

	const handleTableAscDes = (pagination, filters, sorter) => {
		setTableSorter((prev) => ({
			field: sorter.field || sorter.columnKey || prev.field,
			order: sorter.order === "ascend" ? "ASC" : sorter.order === "descend" ? "DESC" : "",
		}));
	};

	useEffect(() => {
		if (!crmGuid) {
			return;
		}

		setIsLoading(true);
		KycService.getPartnerFunctionByCompanyGuidAPI(crmGuid)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (!success) {
					NotificationManager.error(response.data?.message || "Unable to load partner function details.");
					setPartnerFunctionRows([]);
					return;
				}

				const responseData = response.data?.responseData || {};
				const rows = Array.isArray(responseData)
					? responseData
					: responseData.partnerFunctionDetails || responseData.partnerFunctions || responseData.data || [];
				setPartnerFunctionRows(Array.isArray(rows) ? rows : []);
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load partner function details.");
				setPartnerFunctionRows([]);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [crmGuid]);

	useEffect(() => {
		if (!crmGuid) {
			return;
		}

		setIsCardsLoading(true);
		KycService.getKycTileCountsAPI({
			...defaultPayload,
			crmGuid,
		})
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};
					setPartnerFunctionCount(apiData.partnerFunction ? apiData.partnerFunction : {});
					setContactsCount(apiData.contacts ? apiData.contacts : {});
					setMaterialsCount(apiData.materials ? apiData.materials : {});
					setDesignsCount(apiData.designs ? apiData.designs : {});
					setCylindersCount(apiData.cylinders ? apiData.cylinders : {});
					setBusinessOverviewCount(apiData.businessOverview ? apiData.businessOverview : {});
				}
			})
			.catch((error) => {
				console.log(error, "Error");
			})
			.finally(() => {
				setIsCardsLoading(false);
			});
	}, [crmGuid]);

	const sortedPartnerFunctionRows = useMemo(() => {
		if (!tableSorter.field || !tableSorter.order) {
			return partnerFunctionRows;
		}

		const sortedRows = [...partnerFunctionRows];
		sortedRows.sort((leftRow, rightRow) => {
			if (tableSorter.field === "createdOn") {
				const leftDate = parseCreatedOnDate(leftRow.createdOn);
				const rightDate = parseCreatedOnDate(rightRow.createdOn);
				const leftValue = leftDate.isValid() ? leftDate.valueOf() : 0;
				const rightValue = rightDate.isValid() ? rightDate.valueOf() : 0;
				return tableSorter.order === "ASC" ? leftValue - rightValue : rightValue - leftValue;
			}

			const leftValue = String(leftRow?.[tableSorter.field] || "").toLowerCase();
			const rightValue = String(rightRow?.[tableSorter.field] || "").toLowerCase();

			if (leftValue === rightValue) {
				return 0;
			}

			if (tableSorter.order === "ASC") {
				return leftValue > rightValue ? 1 : -1;
			}

			return leftValue < rightValue ? 1 : -1;
		});

		return sortedRows;
	}, [partnerFunctionRows, tableSorter]);

	const tableColumns = useMemo(
		() => [
			{
				title: "SAP Code",
				dataIndex: "ispl_SapCode",
				key: "ispl_SapCode",
				sorter: true,
				field: "ispl_SapCode",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_SapCode"),
				width: 160,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Type (New)",
				dataIndex: "ispl_PType",
				key: "ispl_PType",
				sorter: true,
				field: "ispl_PType",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_PType"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Account Name",
				dataIndex: "ispl_Name",
				key: "ispl_Name",
				sorter: true,
				field: "ispl_Name",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_Name"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Street 1",
				dataIndex: "ispl_Street1",
				key: "ispl_Street1",
				sorter: true,
				field: "ispl_Street1",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_Street1"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Street 2",
				dataIndex: "ispl_Street2",
				key: "ispl_Street2",
				sorter: true,
				field: "ispl_Street2",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_Street2"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "City",
				dataIndex: "ispl_CityName",
				key: "ispl_CityName",
				sorter: true,
				field: "ispl_CityName",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_CityName"),
				width: 140,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Created On",
				dataIndex: "createdOn",
				sorter: true,
				field: "createdOn",
				showSorterTooltip: false,
				key: "createdOn",
				sortOrder: getSortOrder(tableSorter, "createdOn"),
				width: 140,
				render: (value) => <span>{formatDate(value)}</span>,
			},
		],
		[tableSorter],
	);

	const partnerFunctionStats = useMemo(() => {
		const counts = { shipToParty: 0, billToParty: 0, payer: 0 };
		partnerFunctionRows.forEach((row) => {
			const typeValue = String(
				getValue(row, ["typeNew", "type", "partnerType", "ispl_PType", "ispl_Type"]),
			).toLowerCase();
			if (typeValue.includes("ship to party")) counts.shipToParty += 1;
			if (typeValue.includes("bill to party")) counts.billToParty += 1;
			if (typeValue.includes("payer")) counts.payer += 1;
		});
		return counts;
	}, [partnerFunctionRows]);

	const partnerFunctionTileStats = {
		total: getFirstAvailableValue(
			partnerFunctionCount?.partnerTotalCount,
			partnerFunctionCount?.totalCount,
			partnerFunctionCount?.total,
			partnerFunctionRows.length,
		),
		shipToParty: getFirstAvailableValue(
			partnerFunctionCount?.ship_To_Party,
			partnerFunctionStats.shipToParty,
		),
		billToParty: getFirstAvailableValue(
			partnerFunctionCount?.bill_To_Party,
			partnerFunctionStats.billToParty,
		),
		payer: getFirstAvailableValue(partnerFunctionCount?.payer, partnerFunctionStats.payer),
	};

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								<Breadcrumb.Item>
									<label>{soldToPartyName}</label>
								</Breadcrumb.Item>
							</Breadcrumb>
							<div className="kyc-partner-top-actions">
								<DatePicker
									className="kyc-partner-date-control"
									placeholder="Onboard Date"
									value={onboardDate}
									format="DD-MM-YYYY"
									onChange={(value) => setOnboardDate(value)}
								/>
								<DatePicker
									className="kyc-partner-date-control"
									placeholder="Association Year"
									picker="year"
									value={associationYear}
									format="YYYY"
									onChange={(value) => setAssociationYear(value)}
								/>
								<Link to="/kyc?companyTab=account">
									<Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
										Back
									</Button>
								</Link>
							</div>
						</div>
						<Spin size="large" spinning={isLoading || isCardsLoading}>
							<div className="row">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_green2">
										<div className="card-content">
											<div className="kyc-card-body KYCMaterials images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCMaterialsImage} alt="Img" />
														<span>{partnerFunctionTileStats.total || 0} Partner Function</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{partnerFunctionTileStats.shipToParty || "-"}
														</h5>
														<h6 className="text-white mb-0">Ship to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{partnerFunctionTileStats.billToParty || "-"}
														</h5>
														<h6 className="text-white mb-0">Bill to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{partnerFunctionTileStats.payer || "-"}
														</h5>
														<h6 className="text-white mb-0">Payer</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_red">
										<div className="card-content">
											<div className="kyc-card-body KYCContacts images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCContactsImage} alt="Img" />
														<span>
															{getCardTitle(contactsCount, "Contacts", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{contactsCount.active || "-"}
														</h5>
														<h6 className="text-white mb-0">Active</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">{contactsCount.hide || "-"}</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_blue">
										<div className="card-content">
											<div className="kyc-card-body KYCMaterials images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCMaterialsImage} alt="Img" />
														<span>
															{getCardTitle(materialsCount, "Materials", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{materialsCount.running || "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{materialsCount.close || "-"}
														</h5>
														<h6 className="text-white mb-0">Close</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_grey">
										<div className="card-content">
											<div className="kyc-card-body KYCDesign images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCDesignImage} alt="Img" />
														<span>
															{getCardTitle(designsCount, "Designs", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{designsCount.running || "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">{designsCount.hide || "-"}</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple">
										<div className="card-content">
											<div className="kyc-card-body KYCCylinders images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCCylindersImage} alt="Img" />
														<span>
															{getCardTitle(cylindersCount, "Cylinders", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{cylindersCount.running || "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{cylindersCount.close || "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple2">
										<div className="card-content">
											<div className="kyc-card-body KYCBusinessOverview images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCBusinessOverviewImage} alt="Img" />
														<span>
															{getCardTitle(businessOverviewCount, "Business Overview", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{businessOverviewCount.running || "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{businessOverviewCount.hide || "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<Card className="kyc-table-card">
								<h5 className="kyc-company-title mb-4">Partner Function Detail</h5>
								<Tabs
									activeKey={activeTab}
									className="dash_tab_panel kyc_company_tabs"
									type="card"
									onChange={setActiveTab}>
									<TabPane tab="Partner Function" key="partnerFunction">
										<Table
											bordered
											scroll={{ x: 1400 }}
											className="logistic_tables kyc_logistic_tables"
											dataSource={sortedPartnerFunctionRows}
											columns={tableColumns}
											onChange={handleTableAscDes}
											rowKey={(record, index) =>
												record.id || record.crmGuid || record.sapCode || index
											}
											pagination={false}
										/>
									</TabPane>
									<TabPane tab="Material" key="material">
										<div className="kyc-tab-placeholder">
											Material data will appear here once the API is ready.
										</div>
									</TabPane>
									<TabPane tab="Quotation" key="quotation">
										<div className="kyc-tab-placeholder">
											Quotation data will appear here once the API is ready.
										</div>
									</TabPane>
									<TabPane tab="Orders" key="orders">
										<div className="kyc-tab-placeholder">
											Orders data will appear here once the API is ready.
										</div>
									</TabPane>
									<TabPane tab="Design" key="design">
										<div className="kyc-tab-placeholder">
											Design data will appear here once the API is ready.
										</div>
									</TabPane>
									<TabPane tab="Credit Detail" key="creditDetail">
										<div className="kyc-tab-placeholder">
											Credit detail data will appear here once the API is ready.
										</div>
									</TabPane>
									<TabPane tab="Cardiogram" key="cardiogram">
										<div className="kyc-tab-placeholder">
											Cardiogram data will appear here once the API is ready.
										</div>
									</TabPane>
								</Tabs>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycPartnerFunctionDetails;
